import React from 'react'
import { Modal, CurrencyInput } from '@/shared/components/ui'
import { useTranslation } from '@/shared/contexts/TranslationContext'
import type { AnyEntry } from '@/modules/billing/entries/entriesTypes'

interface Props {
  isOpen: boolean
  entry: AnyEntry | null
  amount: string
  date: string
  submitting: boolean
  onClose: () => void
  onConfirm: () => void
  setAmount: (v: string) => void
  setDate: (v: string) => void
  isMobile?: boolean
}

export default function PayoffModal({ isOpen, entry, amount, date, submitting, onClose, onConfirm, setAmount, setDate, isMobile = false }: Props) {
  const { t, locale } = useTranslation()
  const te = (k: string, p?: Record<string, string>) => t(`billing.entries.${k}`, p)
  if (!entry) return null

  const paid = Number(amount)
  const remainder = entry.amount - paid
  const isPartial = paid > 0 && paid < entry.amount
  const isValid = paid > 0 && paid <= entry.amount && !!date

  const currency = (() => {
    try {
      const opts = new Intl.NumberFormat(locale).resolvedOptions()
      return (opts && (opts as any).currency) || 'BRL'
    } catch (e) { return 'BRL' }
  })()

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => !submitting && onClose()}
      title={te('payoff.title')}
      size="sm"
      mobileTopAligned={false}
      headerLeft={
        isMobile ? (
          <button
            onClick={() => !submitting && onClose()}
            disabled={submitting}
            className="text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-slate-100 transition-colors"
            aria-label={t('actions.cancel') || 'Cancelar'}
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        ) : undefined
      }
      headerRight={
        <div className="flex items-center gap-3">
          <button
            onClick={onConfirm}
            disabled={submitting || !isValid}
            className="flex items-center gap-2 text-emerald-600 dark:text-emerald-300 hover:text-emerald-800 dark:hover:text-emerald-100 disabled:opacity-40 transition-colors"
            aria-label="Confirmar"
            title={isPartial ? te('payoff.confirmPartial') : te('payoff.confirm')}
          >
            {submitting ? (
              <svg className="animate-spin w-6 h-6" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : (
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </button>
          <button
            onClick={() => !submitting && onClose()}
            disabled={submitting}
            className="flex items-center gap-2 text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-white transition-colors"
            aria-label={t('actions.cancel') || 'Fechar'}
          >
            {!isMobile && (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
          </button>
        </div>
      }
    >
      <div className="space-y-5">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
            <svg className="h-5 w-5 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="min-w-0">
            <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400 truncate">{entry.description}</p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            {te('payoff.amountLabel')}
            <span className="ml-2 text-xs font-normal text-slate-400">({te('payoff.amountTotal', { amount: new Intl.NumberFormat(locale, { style: 'currency', currency }).format(entry.amount) })})</span>
          </label>
          <CurrencyInput
            value={amount}
            onChange={setAmount}
            locale={locale}
            disabled={submitting}
            error={paid > entry.amount}
          />
          {paid > entry.amount && (
            <p className="mt-1 text-xs text-rose-600">{te('payoff.amountMaxError')}</p>
          )}
        </div>

        {isPartial && (
          <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 px-4 py-3 flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-amber-700 dark:text-amber-400">{te('payoff.partialTitle')}</p>
              <p className="text-xs text-amber-600 dark:text-amber-500 mt-0.5">{te('payoff.partialDescription', { amount: new Intl.NumberFormat(locale, { style: 'currency', currency }).format(remainder) })}</p>
            </div>
            <span className="text-base font-bold text-amber-700 dark:text-amber-300">{new Intl.NumberFormat(locale, { style: 'currency', currency }).format(remainder)}</span>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            {te('payoff.dateLabel')}
          </label>
          <input
            type="date"
            lang={locale}
            value={date}
            onChange={(e) => setDate(e.target.value)}
            disabled={submitting}
            className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>
      </div>
    </Modal>
  )
}
